import type { FormEventHandler } from "react"

import { Button, Input, Label } from "@coval/ui"

import type { InvitationProfileField } from "../utils/invitationSignUp"

interface ClerkInvitationProfileStepProps {
  error: string | null
  firstName: string
  lastName: string
  requiredFields: InvitationProfileField[]
  submitting: boolean
  onFirstNameChange: (firstName: string) => void
  onLastNameChange: (lastName: string) => void
  onSubmit: FormEventHandler<HTMLFormElement>
}

export function ClerkInvitationProfileStep({
  error,
  firstName,
  lastName,
  requiredFields,
  submitting,
  onFirstNameChange,
  onLastNameChange,
  onSubmit,
}: ClerkInvitationProfileStepProps) {
  const requiresFirstName = requiredFields.includes("first_name")
  const requiresLastName = requiredFields.includes("last_name")

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-6"
      aria-describedby={error ? "signup-error" : undefined}
    >
      <div className="space-y-1">
        <h1 className="text-xl font-medium text-coval-text text-center md:text-left">
          Complete your account
        </h1>
        <p className="text-sm text-coval-text-muted">
          Add the information required to accept your organization invitation.
        </p>
      </div>

      {requiresFirstName && (
        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signup-first-name">
            First name
          </Label>
          <Input
            id="signup-first-name"
            name="given-name"
            autoComplete="given-name"
            value={firstName}
            onChange={(event) => onFirstNameChange(event.target.value)}
            required
            aria-label="First name"
          />
        </div>
      )}

      {requiresLastName && (
        <div className="space-y-1">
          <Label className="font-medium" htmlFor="signup-last-name">
            Last name
          </Label>
          <Input
            id="signup-last-name"
            name="family-name"
            autoComplete="family-name"
            value={lastName}
            onChange={(event) => onLastNameChange(event.target.value)}
            required
            aria-label="Last name"
          />
        </div>
      )}

      {error && (
        <p
          id="signup-error"
          role="alert"
          className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500"
        >
          {error}
        </p>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={
          submitting ||
          (requiresFirstName && !firstName.trim()) ||
          (requiresLastName && !lastName.trim())
        }
        aria-label="Continue"
      >
        {submitting ? "Saving…" : "Continue"}
      </Button>
    </form>
  )
}
